/**
 * FundManagementPage — 4-wallet branch fund system
 *
 * Wallet types and their flow rules:
 *   Cashier  → receives cash/check sales; used for expenses/POs; admin capital add only
 *   Safe     → receives close-day transfers; cashier↔safe via manager PIN
 *   Digital  → receives GCash/Maya/digital sales; audit-only, no direct spend
 *   Bank     → receives safe→bank deposits; admin TOTP required; balance hidden from non-admins
 *
 * Allowed transfers (all require authorization + full audit trail):
 *   Cashier → Safe      Manager PIN
 *   Safe → Cashier      Manager PIN
 *   Safe → Bank         TOTP (Time-Based PIN)
 *   Capital (→Cashier)  Admin only
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import FundTransferDialog from '../components/FundTransferDialog';
import OverageReserveCard from '../components/OverageReserveCard';
import {
  Banknote, Lock, Smartphone, Building2, RefreshCw, ArrowRight,
  ArrowRightLeft, Shield, EyeOff, History, AlertTriangle, CalendarDays,
  Users, Clock
} from 'lucide-react';
import { localTodayStr } from '../lib/dateFormat';
import { toast } from 'sonner';
import { formatPHP, fmtDateTime } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WALLET_META = {
  cashier: {
    label: 'Cashier Drawer',
    icon: Banknote,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    desc: 'Cash & check sales. Expenses & PO payments.',
  },
  safe: {
    label: 'Physical Safe',
    icon: Lock,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    desc: 'Close-day transfers from cashier. Can pay expenses.',
  },
  digital: {
    label: 'Digital / E-Wallet',
    icon: Smartphone,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    desc: 'GCash, Maya, Bank Transfer. Audit trail only.',
  },
  bank: {
    label: 'Bank Account',
    icon: Building2,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    desc: 'Safe→Bank deposits with TOTP (time-based PIN). Balance confidential.',
  },
};

const TRANSFER_TYPES = [
  {
    key: 'cashier_to_safe',
    label: 'Cashier → Safe',
    desc: 'Move cash from drawer to safe at end of shift',
    auth: 'Manager PIN',
    icon: '🔒',
    from: 'cashier', to: 'safe',
  },
  {
    key: 'safe_to_cashier',
    label: 'Safe → Cashier',
    desc: 'Pull cash from safe to replenish drawer',
    auth: 'Manager PIN',
    icon: '💵',
    from: 'safe', to: 'cashier',
  },
  {
    key: 'safe_to_bank',
    label: 'Safe → Bank',
    desc: 'Deposit safe balance to bank account',
    auth: 'TOTP',
    icon: '🏦',
    from: 'safe', to: 'bank',
  },
  {
    key: 'capital_add',
    label: 'Capital Injection',
    desc: 'Add operating capital to cashier or safe — requires admin authorization',
    auth: 'Owner PIN or TOTP',
    icon: '💰',
    to: 'cashier',
  },
];

export default function FundManagementPage() {
  const { currentBranch, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [wallets, setWallets] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('overview'); // overview | transfers | history

  // Multi-shift visibility (Phase A)
  const [activeShifts, setActiveShifts] = useState([]);
  const [shiftsEnabled, setShiftsEnabled] = useState(false);
  // Cashier-blind mode: when own shift is active, all balances hidden
  const [blindModeReason, setBlindModeReason] = useState(null);

  // Transfer form state
  const [activeTransfer, setActiveTransfer] = useState(null); // TRANSFER_TYPES entry

  // Wallet movements
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [movements, setMovements] = useState([]);
  const [movLoading, setMovLoading] = useState(false);

  // Fund-transfer date edit
  const [dateEditTransfer, setDateEditTransfer] = useState(null);
  const [dateEditNewDate, setDateEditNewDate] = useState('');
  const [dateEditPin, setDateEditPin] = useState('');
  const [dateEditReason, setDateEditReason] = useState('');
  const [dateEditSubmitting, setDateEditSubmitting] = useState(false);

  const branchId = currentBranch?.id;

  const loadData = useCallback(async () => {
    if (!branchId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [walletsRes, transfersRes, featureRes] = await Promise.all([
        api.get(`${BACKEND_URL}/api/fund-wallets`, { params: { branch_id: branchId } }),
        api.get(`${BACKEND_URL}/api/fund-transfers`, { params: { branch_id: branchId, limit: 20 } }),
        api.get(`${BACKEND_URL}/api/shifts/feature-status`).catch(() => ({ data: { enabled: false } })),
      ]);
      setWallets(walletsRes.data || []);
      setTransfers(transfersRes.data || []);

      const featOn = !!featureRes.data?.enabled;
      setShiftsEnabled(featOn);

      // Cashier on own active shift → balances blinded by backend
      const anyBlind = (walletsRes.data || []).some(w => w.balance_hidden_during_shift);
      setBlindModeReason(anyBlind ? featureRes.data?.active_shift : null);

      // Admin: fetch all active shifts to show per-cashier sub-wallet cards
      if (featOn && isAdmin) {
        try {
          const sumRes = await api.get(`${BACKEND_URL}/api/shifts/active-summary`, {
            params: { branch_id: branchId },
          });
          setActiveShifts(sumRes.data?.items || []);
        } catch { setActiveShifts([]); }
      } else {
        setActiveShifts([]);
      }
    } catch { toast.error('Failed to load fund data'); }
    setLoading(false);
  }, [branchId, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  const walletByType = (type) => wallets.find(w => w.type === type);

  const openTransfer = (ttype) => {
    setActiveTransfer(ttype);
  };

  const loadMovements = async (wallet) => {
    setSelectedWallet(wallet);
    setMovLoading(true);
    try {
      // When multi-shift is on, the wallet's "balance" is the combined pool.
      // Show only main-pool movements so per-cashier sub-drawer rows don't
      // clutter the Main Drawer history view.
      const params = (shiftsEnabled && wallet.multi_shift_split) ? { main_only: true } : {};
      const res = await api.get(`${BACKEND_URL}/api/fund-wallets/${wallet.id}/movements`, { params });
      setMovements(res.data || []);
    } catch {}
    setMovLoading(false);
  };

  // ── Change fund-transfer date (PIN-gated, audit-logged) ──
  const handleEditTransferDate = async () => {
    if (!dateEditTransfer) return;
    if (!dateEditNewDate) { toast.error('Pick a new date'); return; }
    if (dateEditNewDate === dateEditTransfer.date) { toast.error('Pick a different date'); return; }
    if (!dateEditPin || dateEditPin.length < 4) { toast.error('Manager PIN required'); return; }
    setDateEditSubmitting(true);
    try {
      const res = await api.post(`${BACKEND_URL}/api/fund-transfers/${dateEditTransfer.id}/edit-date`, {
        new_date: dateEditNewDate,
        manager_pin: dateEditPin,
        reason: dateEditReason || 'Date corrected',
      });
      toast.success(`${res.data.message} — authorized by ${res.data.authorized_by}`);
      setDateEditTransfer(null);
      setDateEditNewDate('');
      setDateEditPin('');
      setDateEditReason('');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Date change failed');
    }
    setDateEditSubmitting(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-48 text-slate-400">
      <RefreshCw size={18} className="animate-spin mr-2" /> Loading fund data...
    </div>
  );

  if (!branchId) return (
    <div className="flex items-center justify-center h-48 text-slate-400">
      <AlertTriangle size={18} className="mr-2 text-amber-400" /> Please select a branch to view fund management.
    </div>
  );

  // ── Overview ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5 animate-fadeIn" data-testid="fund-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[24px] font-bold tracking-tight" style={{ fontFamily: 'Manrope', color: 'var(--text-primary)' }}>Fund Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">{currentBranch?.name} — 4-wallet system</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw size={13} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Blind-mode banner: cashier on own active shift */}
      {blindModeReason && (
        <Card className="border-2 border-amber-300 bg-amber-50/60" data-testid="fund-blind-banner">
          <CardContent className="p-4 flex items-center gap-3">
            <EyeOff size={20} className="text-amber-600 shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-sm text-amber-900">
                Money details hidden during your active shift
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                For accountability, balances are revealed only at shift close.
                Shift: <span>{blindModeReason.shift_id}</span>
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 4 Wallet Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {['cashier', 'safe', 'digital', 'bank'].map(type => {
          const wallet = walletByType(type);
          const meta = WALLET_META[type];
          const Icon = meta.icon;
          const hidden = wallet?.balance_hidden || wallet?.balance_hidden_during_shift;
          const split = wallet?.multi_shift_split;
          return (
            <Card key={type} className={`border-2 ${meta.border} cursor-pointer hover:shadow-sm transition-shadow rounded-lg`}
              onClick={() => wallet && !hidden && loadMovements(wallet)}
              data-testid={`wallet-card-${type}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-9 h-9 rounded-md ${meta.bg} flex items-center justify-center`}>
                    <Icon size={18} className={meta.color} />
                  </div>
                  {type === 'bank' && !isAdmin && (
                    <Badge className="text-[9px] bg-purple-100 text-purple-600">Confidential</Badge>
                  )}
                  {split && (
                    <Badge className="text-[9px] bg-slate-100 text-slate-600" title="Multi-cashier split view">Main + Subs</Badge>
                  )}
                </div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{meta.label}</p>
                {hidden ? (
                  <div className="flex items-center gap-1.5">
                    <EyeOff size={14} className="text-slate-400" />
                    <span className="text-sm text-slate-400 italic">
                      {wallet?.balance_hidden_during_shift ? 'Hidden during shift' : 'Balance hidden'}
                    </span>
                  </div>
                ) : split ? (
                  <>
                    <p className={`text-xl font-bold ${meta.color}`} style={{ fontFamily: 'Manrope', fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"' }}>
                      {formatPHP(wallet.main_only_balance || 0)}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Main only</p>
                    <div className="mt-1.5 pt-1.5 border-t border-slate-100 text-[10px] text-slate-500 space-y-0.5">
                      <div className="flex justify-between">
                        <span>Sub-drawers ({wallet.active_shifts_count || 0})</span>
                        <span className="font-semibold text-slate-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(wallet.sub_drawers_total || 0)}</span>
                      </div>
                      <div className="flex justify-between text-slate-400">
                        <span>Combined total</span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(wallet.balance || 0)}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className={`text-xl font-bold ${meta.color}`} style={{ fontFamily: 'Manrope', fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"' }}>
                    {wallet ? formatPHP(wallet.balance || 0) : '—'}
                  </p>
                )}
                <p className="text-[10px] text-slate-400 mt-1 leading-tight">{meta.desc}</p>
                {wallet && !hidden && (
                  <button className="text-[10px] text-slate-400 hover:text-slate-600 mt-2 flex items-center gap-1">
                    <History size={10} /> View {split ? 'main' : ''} history
                  </button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Active Cashier Sub-Drawers — admin view only */}
      {shiftsEnabled && isAdmin && (
        <ActiveCashiersSection
          activeShifts={activeShifts}
          onRefresh={loadData}
        />
      )}

      {/* Overage Reserve summary — read-only badge into the Audit Center */}
      <OverageReserveCard branchId={branchId} />

      {/* Movements panel */}
      {selectedWallet && (
        <Card style={{ border: "1px solid var(--border-default)" }}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <History size={14} className="text-slate-500" />
                {WALLET_META[selectedWallet.type]?.label || selectedWallet.name} — Transaction History
              </CardTitle>
              <button onClick={() => setSelectedWallet(null)} className="text-slate-400 hover:text-slate-600 text-xs">Close</button>
            </div>
          </CardHeader>
          <CardContent>
            {movLoading ? (
              <div className="text-center py-4"><RefreshCw size={16} className="animate-spin mx-auto text-slate-400" /></div>
            ) : movements.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">No transactions yet</p>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {movements.map(m => (
                  <div key={m.id} className="flex items-center justify-between text-xs py-1.5 border-b border-slate-50 last:border-0">
                    <div>
                      <span className="text-slate-400 mr-2">{fmtDateTime(m.created_at)}</span>
                      <span className="text-slate-600 truncate max-w-[220px]">{m.reference || m.type}</span>
                      {m.platform && <span className="ml-1.5 text-blue-500 text-[10px]">{m.platform}</span>}
                      {m.ref_number && <span className="ml-1 text-slate-400 text-[10px]">#{m.ref_number}</span>}
                    </div>
                    <span className={` font-semibold shrink-0 ml-3 ${m.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {m.amount >= 0 ? '+' : ''}{formatPHP(m.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Fund Transfers */}
      <Card style={{ border: "1px solid var(--border-default)" }}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <ArrowRightLeft size={16} className="text-[#1A4D2E]" />
            Fund Transfers
          </CardTitle>
          <p className="text-xs text-slate-500">All transfers require authorization and are permanently logged.</p>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-3">
            {TRANSFER_TYPES.map(ttype => (
              <button key={ttype.key} onClick={() => openTransfer(ttype)}
                data-testid={`transfer-btn-${ttype.key}`}
                className="flex items-start gap-3 p-3 rounded-lg border-[var(--border-default)] hover:border-[#1A4D2E]/40 hover:bg-slate-50 transition-all text-left">
                <span className="text-xl shrink-0">{ttype.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-800">{ttype.label}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{ttype.desc}</p>
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 mt-1">
                    <Shield size={10} /> {ttype.auth}
                  </span>
                </div>
                <ArrowRight size={14} className="text-slate-400 shrink-0 mt-1" />
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Transfer History */}
      {transfers.length > 0 && (
        <Card style={{ border: "1px solid var(--border-default)" }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <History size={14} className="text-slate-500" /> Recent Transfers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {transfers.slice(0, 8).map(t => (
                <div key={t.id} className="flex items-center justify-between text-xs py-1.5 border-b border-slate-50 last:border-0" data-testid={`fund-transfer-row-${t.id}`}>
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-slate-700">{t.description}</span>
                    {t.note && <span className="text-slate-400 ml-2">— {t.note}</span>}
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      By {t.performed_by_name} · Auth: {t.authorized_by} · Dated: <span className="text-slate-600">{t.date || '—'}</span> · Entered: {fmtDateTime(t.created_at)}
                    </p>
                    {(t.date_edit_history || []).length > 0 && (
                      <p className="text-[10px] text-indigo-500 mt-0.5">
                        Date edited {t.date_edit_history.length}×
                        {t.date_edit_history[t.date_edit_history.length - 1]?.from && (
                          <> · last change: {t.date_edit_history[t.date_edit_history.length - 1].from} → {t.date_edit_history[t.date_edit_history.length - 1].to}</>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    <Button variant="ghost" size="sm"
                      className="h-6 px-1.5 text-[10px] text-indigo-600 hover:text-indigo-700 gap-0.5"
                      onClick={() => {
                        setDateEditTransfer(t);
                        setDateEditNewDate(t.date || localTodayStr());
                        setDateEditPin('');
                        setDateEditReason('');
                      }}
                      title="Change the effective date of this transfer (PIN-gated)"
                      data-testid={`edit-transfer-date-${t.id}`}>
                      <CalendarDays size={10} /> Date
                    </Button>
                    <span className="font-bold text-[#1A4D2E]">{formatPHP(t.amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transfer Dialog */}
      <FundTransferDialog
        open={!!activeTransfer}
        onClose={() => setActiveTransfer(null)}
        transferType={activeTransfer}
        walletByType={walletByType}
        branchId={branchId}
        onSuccess={loadData}
      />

      {/* Edit Transfer Date Dialog */}
      {dateEditTransfer && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }}
          onClick={e => { if (e.target === e.currentTarget) setDateEditTransfer(null); }}
          data-testid="edit-transfer-date-dialog">
          <div className="bg-white rounded-2xl shadow-2xl w-full p-5" style={{ maxWidth: '400px' }}>
            <div className="flex items-center gap-3 mb-4">
              <CalendarDays size={18} className="text-indigo-600" />
              <div>
                <p className="font-bold text-slate-800">Change Fund Transfer Date</p>
                <p className="text-xs text-slate-400">{dateEditTransfer.description} · {formatPHP(dateEditTransfer.amount)}</p>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1 mb-3">
              <div className="flex justify-between"><span className="text-slate-500">Current Date</span><span className="font-bold">{dateEditTransfer.date || '—'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Authorized By</span><span>{dateEditTransfer.authorized_by || '—'}</span></div>
              <p className="text-[10px] text-slate-500 mt-1.5 leading-snug">
                Both the old and the new date must be OPEN (not included in a Z-Report).
                Audit trail is preserved.
              </p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-600 font-medium">New Date *</label>
                <input type="date" value={dateEditNewDate}
                  onChange={e => setDateEditNewDate(e.target.value)}
                  max={localTodayStr()}
                  className="mt-1 w-full h-9 px-3 rounded-md border border-slate-200 text-sm"
                  data-testid="edit-transfer-date-newdate" />
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium">Reason (optional)</label>
                <input type="text" value={dateEditReason}
                  onChange={e => setDateEditReason(e.target.value)}
                  placeholder="e.g. Recorded under wrong day"
                  className="mt-1 w-full h-9 px-3 rounded-md border border-slate-200 text-sm"
                  data-testid="edit-transfer-date-reason" />
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium flex items-center gap-1"><Shield size={11} /> Manager PIN *</label>
                <input type="password" value={dateEditPin} autoComplete="one-time-code" data-lpignore="true"
                  onChange={e => setDateEditPin(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleEditTransferDate()}
                  placeholder="Enter PIN"
                  className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 font-mono text-center text-xl tracking-widest"
                  data-testid="edit-transfer-date-pin" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setDateEditTransfer(null)}
                className="flex-1 py-2.5 rounded-lg border-[var(--border-default)] text-sm text-slate-600 hover:bg-slate-50"
                data-testid="edit-transfer-date-cancel">Cancel</button>
              <button onClick={handleEditTransferDate}
                disabled={dateEditSubmitting || !dateEditNewDate || dateEditNewDate === dateEditTransfer.date || !dateEditPin || dateEditPin.length < 4}
                className="flex-1 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50"
                data-testid="edit-transfer-date-confirm">
                {dateEditSubmitting ? 'Saving...' : 'Confirm Change'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── ActiveCashiersSection ────────────────────────────────────────────────────
// Surfaces every currently-open shift at the branch with its live sub-wallet
// balances, sales totals, and drops. Admin/owner only — cashiers never see
// other cashiers' running totals (intentional blind-count accountability).
function ActiveCashiersSection({ activeShifts, onRefresh }) {
  if (!activeShifts || activeShifts.length === 0) {
    return (
      <Card className="border-dashed border-2 border-slate-200" data-testid="active-cashiers-empty">
        <CardContent className="py-6 text-center">
          <Users size={20} className="text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No active cashier shifts</p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            When a cashier opens a shift, their virtual drawer will appear here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card style={{ border: "1px solid var(--border-default)" }} data-testid="active-cashiers-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <Users size={16} className="text-[#1A4D2E]" />
          Active Cashiers
          <Badge className="text-[10px] bg-emerald-100 text-emerald-700 ml-1">{activeShifts.length} open</Badge>
        </CardTitle>
        <p className="text-xs text-slate-500">
          Each cashier's virtual sub-wallet. Balances funnel back to Main on shift close.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {activeShifts.map(s => {
            const total = (s.cash_sub_balance || 0) + (s.digital_sub_balance || 0);
            return (
              <div key={s.shift_id} className="border border-slate-200 rounded-xl p-3 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all"
                data-testid={`active-cashier-${s.cashier_id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm text-slate-800 truncate">{s.cashier_name}</p>
                    <p className="text-[10px] text-slate-400">{s.shift_id}</p>
                  </div>
                  <Badge className="text-[9px] bg-emerald-100 text-emerald-700 shrink-0">
                    <Clock size={9} className="mr-0.5" />
                    {s.opened_at ? new Date(s.opened_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                  </Badge>
                </div>

                <div className="space-y-1 text-[11px]">
                  <div className="flex justify-between text-slate-500">
                    <span>Opening float</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(s.opening_float || 0)}</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>Sales ({s.invoice_count || 0})</span>
                    <span className="text-emerald-700 font-medium" style={{ fontVariantNumeric: 'tabular-nums' }}>+{formatPHP(s.gross_sales || 0)}</span>
                  </div>
                  {(s.returns_and_outflows || 0) > 0 && (
                    <div className="flex justify-between text-slate-500">
                      <span>Returns / out</span>
                      <span className="text-red-600 font-medium" style={{ fontVariantNumeric: 'tabular-nums' }}>−{formatPHP(s.returns_and_outflows)}</span>
                    </div>
                  )}
                  {s.drops_total > 0 && (
                    <div className="flex justify-between text-slate-500">
                      <span>Drops</span>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>−{formatPHP(s.drops_total)}</span>
                    </div>
                  )}
                </div>

                <div className="mt-2 pt-2 border-t border-slate-100 grid grid-cols-2 gap-2">
                  <div className="text-center">
                    <p className="text-[9px] text-slate-400 uppercase tracking-wider">Cash drawer</p>
                    <p className="text-sm font-bold text-emerald-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(s.cash_sub_balance || 0)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[9px] text-slate-400 uppercase tracking-wider">Digital</p>
                    <p className="text-sm font-bold text-blue-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(s.digital_sub_balance || 0)}</p>
                  </div>
                </div>
                <div className="mt-1 text-center">
                  <span className="text-[10px] text-slate-400">Combined: </span>
                  <span className="font-semibold text-slate-700 text-[11px]" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(total)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
